"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
	type EditorialNavLink,
	type EditorialPlaceNav,
	isNavLinkActive,
} from "@/components/editorial-system/editorial-nav-shared";
import { cn } from "@/lib/utils";

interface EditorialNavListProps {
	links: EditorialNavLink[];
	place?: EditorialPlaceNav;
	/** Extra links nested under the active place link, e.g. chat channels. */
	placeChildren?: EditorialNavLink[];
	className?: string;
	itemClassName?: string;
	label: string;
}

/**
 * The primary navigation in caps. The current section is underlined; on place
 * pages the place and its subpages nest under their section.
 */
export function EditorialNavList({
	links,
	place,
	placeChildren,
	className,
	itemClassName = "ed-nav",
	label,
}: EditorialNavListProps) {
	const pathname = usePathname() ?? "/";

	return (
		<nav aria-label={label}>
			<ul className={cn("ed-nav-list", className)}>
				{links.map((link) => {
					const active = isNavLinkActive(link, pathname);
					const showPlace = place && link.href === place.parentHref;
					return (
						<li key={link.href}>
							<Link
								href={link.href}
								aria-current={active && !showPlace ? "page" : undefined}
								className={cn(itemClassName, active && "ed-active")}
							>
								{link.label}
							</Link>
							{showPlace ? (
								<ul className="ed-place-nav">
									{place.flat ? null : (
										<li className="ed-place-nav__title">
											<Link
												href={place.href}
												aria-current="true"
												className="ed-nav ed-active"
											>
												{place.title}
											</Link>
										</li>
									)}
									{place.links.map((sub) => {
										const subActive = isNavLinkActive(sub, pathname);
										return (
											<li
												key={sub.href}
												className={
													place.flat
														? "ed-place-nav__title"
														: "ed-place-nav__item"
												}
											>
												<Link
													href={sub.href}
													aria-current={subActive ? "page" : undefined}
													className={cn("ed-nav", subActive && "ed-active")}
												>
													{sub.label}
												</Link>
												{subActive && placeChildren?.length ? (
													<ul className="ed-place-nav">
														{placeChildren.map((child) => (
															<li
																key={child.href}
																className="ed-place-nav__item"
															>
																<Link
																	href={child.href}
																	className={cn(
																		"ed-nav",
																		isNavLinkActive(child, pathname) &&
																			"ed-active",
																	)}
																>
																	{child.label}
																</Link>
															</li>
														))}
													</ul>
												) : null}
											</li>
										);
									})}
								</ul>
							) : null}
						</li>
					);
				})}
			</ul>
		</nav>
	);
}

/** Sideways place navigation for the phone top band. */
export function EditorialPlaceBand({ place }: { place: EditorialPlaceNav }) {
	const pathname = usePathname() ?? "/";
	return (
		<nav aria-label={`${place.title} sections`} className="ed-band__sideways">
			{place.links.map((sub) => {
				const active = isNavLinkActive(sub, pathname);
				return (
					<Link
						key={sub.href}
						href={sub.href}
						aria-current={active ? "page" : undefined}
						className={cn("ed-ui", active && "ed-active")}
					>
						{sub.label}
					</Link>
				);
			})}
		</nav>
	);
}
